import { useDispatch, useSelector } from 'react-redux'
import {
  connectorSessionIdSelector,
  connectorCartProductsSelector,
  connectorAfterCreateClaimRecord,
  connectorOrderCompleteSelector,
} from '@redux/slices/connectorSlice'
import { claimRecordIdSelector, setInitiateClaimError } from '@redux/slices/claimStatusSlice'
import { useLazyGetCartItemsQuery } from '@redux/queries/cart'
import { useLazyGetCustomerContextQuery, useLazyGetSessionQuery } from '@redux/queries/sessions'
import { useCreateClaimRecordMutation, useUpdateClaimRecordMutation } from '@redux/queries/claimRecords'
import { Session } from '@typesApp/Session'
import { ClaimData } from '@typesApp/ClaimData'
import { CartBundle } from '@typesApp/Product'
import { ClaimRecord, CreateClaimRecordPayload, CreateClaimRecordResult } from '@typesApp/ClaimRecord'
import { formatCommerceCode } from '@utils/formatCommerceCode'
import { useEnvironment } from './useEnvironment'

type CreateOptions = {
  insurerId?: string
}

type CreateClaimRecordParams = {
  session: Session
  options?: CreateOptions
}

type UseCreateClaimRecordReturn = {
  isLoading: boolean
  isCreating: boolean
  isError: boolean
  createClaimRecord: (options?: CreateOptions) => Promise<CreateClaimRecordResult>
  patchClaimRecord: (claimData: ClaimData, id?: string) => Promise<ClaimRecord>
}

export const useCreateClaimRecord = (): UseCreateClaimRecordReturn => {
  const dispatch = useDispatch()
  const { brand, ecommerceCode, ecommerceTransactionHost, lang, country } = useEnvironment()

  const claimRecordId = useSelector(claimRecordIdSelector)
  const sessionId = useSelector(connectorSessionIdSelector)
  const isOrderComplete = useSelector(connectorOrderCompleteSelector)
  const cartProducts = useSelector(connectorCartProductsSelector)

  const [fetchSession, { isLoading: isSessionLoading }] = useLazyGetSessionQuery()
  const [fetchCustomerContext, { isLoading: isCustomerContextLoading }] = useLazyGetCustomerContextQuery()
  const [fetchCartItems, { isLoading: isCartLoading }] = useLazyGetCartItemsQuery()
  const [triggerCreate, { isLoading: isCreateLoading, isError: isCreateError }] = useCreateClaimRecordMutation()
  const [triggerPatch, { isLoading: isUpdating, isError: isUpdateError }] = useUpdateClaimRecordMutation()

  const isInitialLoading = isCustomerContextLoading || isSessionLoading || isCartLoading
  const isCreating = isCreateLoading || isInitialLoading
  const isLoading = isUpdating || isInitialLoading
  const isError = isCreateError || isUpdateError

  const createClaimRecord = async (options?: CreateOptions): Promise<CreateClaimRecordResult> => {
    if (claimRecordId) {
      return { id: claimRecordId }
    }

    try {
      // always fetch latest session id and customer
      const customerContext = await fetchCustomerContext().unwrap()
      const session = await fetchSession(customerContext?.sessionId || sessionId).unwrap()
      if (!session?.customer?.id) {
        throw new Error('Customer number empty - cannot create claim record')
      }

      // Note: use latest sessionId from customer context to ensure we do not
      // create claim record with an expired sessionId. This happens with
      // partially authenticated sessions where the sessionId has expired but
      // commerce session is still (partially) active.

      const latestSession = {
        ...session,
        ...customerContext,
      }

      return createNewClaimRecord({ session: latestSession, options })
    } catch (error) {
      dispatch(setInitiateClaimError(true))
      throw error
    }
  }

  const createNewClaimRecord = async (params: CreateClaimRecordParams) => {
    const useCartItems = Boolean(params.session?.cartId) && !isOrderComplete
    if (useCartItems) {
      return createClaimRecordWithCartItems(params)
    } else {
      return createClaimRecordDefault(params)
    }
  }

  const createClaimRecordWithCartItems = async (params: CreateClaimRecordParams): Promise<CreateClaimRecordResult> => {
    const { session, options } = params
    let cartBundle: CartBundle

    try {
      cartBundle = await fetchCartItems().unwrap()
    } catch (error) {
      // create claim record w/o cart products on cart items error
      return await createClaimRecordDefault(params)
    }

    const payload = getPayloadFromCartItems(session, cartBundle, options)
    const result = await triggerCreate(payload).unwrap()
    dispatch(connectorAfterCreateClaimRecord(cartBundle))
    return result
  }

  const getPayloadFromCartItems = (
    session: Session,
    cartBundle: CartBundle,
    options?: CreateOptions
  ): CreateClaimRecordPayload => {
    return {
      ...getDefaultPayload(session, options),

      // Note: cartBundle.cartId may be undefined for
      // empty cart but commerce F/E provided a cartId
      cartId: cartBundle.cartId || session.cartId,
      eligibleProducts: cartBundle.products ?? [],
      orderSubtotal: cartBundle.orderSubtotal,
      shippingCosts: cartBundle.shippingCosts,
    }
  }

  const getDefaultPayload = (session: Session, options?: CreateOptions): CreateClaimRecordPayload => {
    const customer = session?.customer

    let payload: CreateClaimRecordPayload = {
      cartId: session?.cartId,
      sessionId: session?.sessionId,
      customerNumber: customer?.id,
      customerEmail: customer?.email,
      customerPhone: customer?.phoneNumber,
      customerName: {
        firstName: customer?.firstName,
        lastName: customer?.lastName,
      },
      eligibleProducts: cartProducts ?? [],
      locale: `${lang}_${country?.toUpperCase()}`,
      ecommerce: formatCommerceCode({
        brand,
        country,
        ecommerceCode,
        ecommerceTransactionHost,
      }),
    }

    // NOTE: remove cart id if order was just completed
    // this may happen if sync insurance is triggered
    // immmediately after order complete
    if (!session?.cartId || isOrderComplete) {
      delete payload.cartId
      payload.eligibleProducts = []
    }

    if (session?.isGuest) {
      delete payload.customerName
      delete payload.customerEmail
      delete payload.customerPhone
    }

    // do not send empty name
    const emptyName = !customer?.firstName && !customer?.lastName
    if (emptyName) {
      delete payload.customerName
    }

    if (options?.insurerId) {
      payload = {
        ...payload,
        claimData: {
          primaryCoverage: {
            insurer: {
              id: options?.insurerId ?? '',
            },
          },
        },
      }
    }

    return payload
  }

  const createClaimRecordDefault = async (params: CreateClaimRecordParams): Promise<CreateClaimRecordResult> => {
    const { session, options } = params
    const payload = getDefaultPayload(session, options)
    const result = await triggerCreate(payload).unwrap()
    dispatch(connectorAfterCreateClaimRecord())
    return result
  }

  /**
   * Patch claim record - the provided id has precedence over the claimRecordId from redux state
   * The id parameter is useful when we need to create claim record and then immediately patch.
   */
  const patchClaimRecord = (claimData: ClaimData, id?: string): Promise<ClaimRecord> => {
    return triggerPatch({
      id: id || claimRecordId,
      claimData,
    }).unwrap()
  }

  return {
    isCreating,
    isLoading,
    isError,
    createClaimRecord,
    patchClaimRecord,
  }
}
